import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { deckService } from "@/services/firestore";
import { Deck } from "@/types";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Plus, BookOpen, MoreVertical, Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

export default function DecksPage() {
  const { user, loading: authLoading } = useAuthStore();
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (user) {
      const fetchDecks = async () => {
        try {
          const data = await deckService.getDecks(user.uid);
          setDecks(data);
        } catch (error) {
          console.error("Error fetching decks:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchDecks();
    }
  }, [user]);

  const filteredDecks = decks.filter(deck => 
    deck.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (deck.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto py-8 md:py-12 px-4 md:px-6">
      <Helmet>
        <title>Bộ thẻ của tôi | Flashcard Platform</title>
      </Helmet>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bộ thẻ của tôi</h1>
          <p className="text-muted-foreground">Quản lý các bộ sưu tập và bắt đầu học tập.</p>
        </div>
        <Link 
          to="/decks/new" 
          className={cn(buttonVariants(), "gap-2")}
        >
          <Plus className="h-4 w-4" /> Tạo bộ thẻ
        </Link>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Tìm kiếm bộ thẻ..." 
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button variant="outline" size="icon">
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {filteredDecks.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDecks.map((deck) => (
            <Card key={deck.id} className="group hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex flex-row justify-between items-start">
                  <div className="p-2 rounded-xl bg-primary/10 text-primary">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </div>
                <CardTitle className="mt-4">{deck.title}</CardTitle>
                <CardDescription className="line-clamp-2 min-h-[40px]">
                  {deck.description || "Chưa có mô tả."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center text-sm text-muted-foreground gap-4">
                  <span>{deck.cardCount || 0} cards</span>
                  {deck.isPublic && (
                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-medium">
                      Public
                    </span>
                  )}
                </div>
              </CardContent>
              <CardFooter className="pt-2 gap-2">
                <Link 
                  to={`/decks/${deck.id}`} 
                  className={cn(buttonVariants({ variant: "outline" }), "flex-1")}
                >
                  Chi tiết
                </Link>
                <Link 
                  to={`/decks/${deck.id}/study`} 
                  className={cn(buttonVariants(), "flex-1")}
                >
                  Học ngay
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed">
          <div className="p-4 rounded-full bg-muted mb-4">
            <BookOpen className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold">Chưa có bộ thẻ nào</h3>
          <p className="text-muted-foreground mb-6 max-w-sm">
            Tạo bộ sưu tập thẻ đầu tiên của bạn để bắt đầu hành trình học tập.
          </p>
          <Link 
            to="/decks/new" 
            className={cn(buttonVariants())}
          >
            Tạo bộ thẻ đầu tiên
          </Link>
        </Card>
      )}
    </div>
  );
}
